import { useCallback } from "react";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { appUpgradeLoadingState, planPickerDrawerOpenState, userState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import { UpgradeLocation, trackUpgradeClick } from "@/lib/tracking";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";

/**
 * The way in from a locked surface, routed by who is looking: a visitor registers (an upgrade
 * flow has no account to attach to), a subscriber in the app gets the plan picker, a subscriber
 * on the web gets the upgrade flow. Same routing as ObfuscationBanner and BondsLocked; use this
 * for new CTAs instead of repeating it.
 *
 * `label` is the button text for the viewer's plan; `extra` (and `more`, per click) go into the
 * tracked click.
 */
const useUpgradeCta = (location: UpgradeLocation, extra: Record<string, any> = {}) => {
    const user = useRecoilValue(userState);
    const loading = useRecoilValue(appUpgradeLoadingState);
    const setPlanPickerOpen = useSetRecoilState(planPickerDrawerOpenState);
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const { push } = useRouter();

    const plan = user?.subscription_plan;
    const isVisitor = !user || !plan || plan === "ORTEX Visitor";
    const label = isVisitor ? "Create free account" : plan === "ORTEX Basic" ? "Get Advanced" : "Upgrade now";

    const extraKey = JSON.stringify(extra);
    const onClick = useCallback(
        (e?: { stopPropagation?: () => void }, more: Record<string, any> = {}) => {
            e?.stopPropagation?.();
            trackUpgradeClick(location, { plan, ...JSON.parse(extraKey), ...more });
            if (isVisitor) push("/login?register=true");
            else if (isOrtexApp()) setPlanPickerOpen(true);
            else handleAppSubscriberUpgrade();
        },
        [location, plan, extraKey, isVisitor, push, setPlanPickerOpen, handleAppSubscriberUpgrade]
    );

    return { isVisitor, plan, label, loading, onClick };
};

export default useUpgradeCta;
